/* eslint-disable prefer-const */
import { IJobParam, IJobParamCreateLoadOptions, IJobParamManager, IJobParamScope } from "./PA.Job.IF.JobParam";
import { PAdbContext } from "../PA.Core/PAdbContext";
import * as _ from "lodash";
import {
    WB_CompanyRow,
    WB_JobParamRow,
    WB_MasterDataSetRow,
    WB_SalesOrgRow,
    WB_UIAppExecutableRow,
} from "../PA.Core/PAdbSchemaRows";
import {
    esWB_Company,
    esWB_Job_Param,
    esWB_Job_ParamSR,
    esWB_MasterData_Set,
    esWB_SalesOrg,
    esWB_UI_AppExecutable,
} from "../PA.Core/PAdbSchema";
import { JobParam } from "./JobParam";
import { ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";

export class JobParamManager implements IJobParamManager {
    dbContext: PAdbContext;

    constructor() {}

    private getDbContext(): PAdbContext {
        if (!_.isEmpty(this.dbContext)) return this.dbContext;

        this.dbContext = new PAdbContext();
        return this.dbContext;
    }

    async findByWbId(wbJobParamId: number, options?: { dbContext: PAdbContext }): Promise<IJobParam> {
        let dbContext = options?.dbContext;
        if (_.isEmpty(dbContext)) dbContext = this.getDbContext();

        const jobParamRows = await this.findByCondition(
            {
                WB_JOB_PARAM_ID: wbJobParamId,
                DELETED_IND: { neq: "1" },
            },
            { dbContext: dbContext },
        );

        if (_.isEmpty(jobParamRows)) return undefined;

        let jobParamRow = _.first(jobParamRows);
        return await this.createFromExistingRow(jobParamRow, { skipRecordResolution: true });
    }

    async findByEnterpriseScope(scope?: IJobParamScope, options?: { dbContext?: PAdbContext }): Promise<IJobParam[]> {
        let dbContext: PAdbContext = options?.dbContext;
        if (_.isEmpty(dbContext)) dbContext = new PAdbContext();

        let s = scope || {};

        let queryCnd: any = { DELETED_IND: { neq: "1" } };
        if (_.isNumber(s.wbMDSetId)) queryCnd.WB_MDSET_ID = s.wbMDSetId;

        if (_.isNumber(s.wbCompanyId)) queryCnd.WB_COMPANY_ID = s.wbCompanyId;

        if (_.isNumber(s.wbSalesOrgId)) queryCnd.WB_SALESORG_ID = s.wbSalesOrgId;

        if (!_.isEmpty(s.sourceType)) queryCnd.SOURCE_TYPE = s.sourceType;

        if (!_.isEmpty(s.jobModuleId)) queryCnd.JOB_MODULE_ID = s.jobModuleId;

        let jobParamRows = await this.findByCondition(queryCnd, { dbContext: dbContext });

        if (_.isEmpty(jobParamRows)) return [];

        let retVal: IJobParam[] = [];

        for (let row of jobParamRows) {
            let jobParam = await this.createFromExistingRow(row, { skipRecordResolution: true });
            retVal.push(jobParam);
        }
        return retVal;
    }

    async findBySourceTypeAndProcessorType(
        sourceType: string,
        sourceTypeWbId: number,
        jobProcessorType: string,
        options: { dbContext: PAdbContext },
    ): Promise<IJobParam> {
        let dbContext: PAdbContext = options?.dbContext;
        if (_.isEmpty(dbContext)) dbContext = new PAdbContext();

        let jobParamRows = await this.findByCondition(
            {
                SOURCE_TYPE: sourceType,
                SOURCE_WB_ID: sourceTypeWbId,
                JOB_PROCESSOR_TYPE: jobProcessorType,
                DELETED_IND: { neq: "1" },
            },
            { dbContext: dbContext },
        );

        if (_.isEmpty(jobParamRows)) return undefined;

        let jobParamRow = _.first(jobParamRows);
        return await this.createFromExistingRow(jobParamRow, { skipRecordResolution: true });
    }

    private async createFromExistingRow(
        pJobParamRow: WB_JobParamRow,
        options?: { skipRecordResolution?: boolean },
    ): Promise<IJobParam> {
        // Use the record Resolver yo
        if (_.isEmpty(pJobParamRow)) return undefined;

        let aJP = new JobParam(pJobParamRow, { skipRecordResolution: !!options?.skipRecordResolution });
        await aJP.OnLoadComplete();

        return aJP;
    }

    async findOrCreate(options: IJobParamCreateLoadOptions): Promise<IJobParam> {
        let dbContext: PAdbContext = options?.dbContext;
        if (_.isEmpty(dbContext)) dbContext = this.getDbContext();

        let rv: IJobParam = undefined;

        // Try to find by id, metas, else create..
        let wbJobParamId = +options.JobParamWbId;
        if (!_.isNaN(wbJobParamId) && _.isNumber(wbJobParamId)) {
            rv = await this.findByWbId(wbJobParamId, { dbContext: dbContext });
            if (!_.isEmpty(rv)) {
                await this.syncOptions(rv.WB_JobParamRow, options);
                return rv;
            }
        }

        let sourceWbId = +options.sourceWbId;
        let sourceType = options.sourceType;
        const jobProcessorType: string = options.JobProcessorType;

        if (_.isNumber(sourceWbId) && !_.isEmpty(sourceType) && !_.isEmpty(jobProcessorType)) {
            rv = await this.findBySourceTypeAndProcessorType(sourceType, sourceWbId, jobProcessorType, {
                dbContext: dbContext,
            });
            if (!_.isEmpty(rv)) {
                await this.syncOptions(rv.WB_JobParamRow, options);
                return rv;
            }
        }

        // Create a Record
        let newJobParam = <WB_JobParamRow>await dbContext.createRecordWithKey(esWB_Job_Param, {});
        await this.syncOptions(newJobParam, options);
        let newJP = new JobParam(newJobParam);
        await newJP.OnLoadComplete();

        return newJP;
    }

    private async syncOptions(pJobParamRow: WB_JobParamRow, opts: IJobParamCreateLoadOptions): Promise<void> {
        if (_.isEmpty(pJobParamRow)) return;

        let dbContext = opts.dbContext;
        if (_.isEmpty(dbContext)) dbContext = this.getDbContext();

        let jpr = pJobParamRow.cif;
        jpr.JOB_PARAM_ID = opts.JobParamId || jpr.JOB_PARAM_ID;
        jpr.JOB_PARAM_NAME = opts.JobParamName || jpr.JOB_PARAM_NAME;
        jpr.WB_MDSET_ID = opts.wbMasterDataSetId || jpr.WB_MDSET_ID;
        jpr.WB_COMPANY_ID = opts.wbCompanyId || jpr.WB_COMPANY_ID;
        jpr.WB_SALESORG_ID = opts.wbSalesOrgId || jpr.WB_SALESORG_ID;
        jpr.SOURCE_WB_ID = opts.sourceWbId || jpr.SOURCE_WB_ID;
        jpr.SOURCE_TYPE = opts.sourceType || jpr.SOURCE_TYPE;
        jpr.WB_ID_TARGET = opts.wbTargetId || jpr.WB_ID_TARGET;
        jpr.JOB_PROCESSOR_TYPE = opts.JobProcessorType || jpr.JOB_PROCESSOR_TYPE;
        jpr.JOB_MODULE_ID = opts.JobSchedulerModuleId || jpr.JOB_MODULE_ID;
        jpr.DEPENDENT_TRIGGER_ID = opts.DependentTriggerId || jpr.DEPENDENT_TRIGGER_ID;
        jpr.JOB_OPTIONS = opts.jobOptions;

        if (_.isBoolean(opts.bDisabled)) jpr.DISABLED_IND = opts.bDisabled ? "1" : "0";

        // Check the FK for Master Data Set / Company / Sales Org / App Exec
        //--- 1. Master Data Set
        if (_.isEmpty(pJobParamRow.WB_MasterDataSetRow)) {
            let mdr = <WB_MasterDataSetRow>await dbContext.find(esWB_MasterData_Set, jpr.WB_MDSET_ID);
            if (!_.isEmpty(mdr)) {
                pJobParamRow.cif.WB_MDSET_ID = mdr.cif.WB_MDSET_ID;
                pJobParamRow.WB_MasterDataSetRow = mdr;
            }
        }

        //--- 2. Company
        if (_.isEmpty(pJobParamRow.WB_CompanyRow)) {
            let companyRow: WB_CompanyRow;
            if (_.isNumber(jpr.WB_COMPANY_ID) && jpr.WB_COMPANY_ID > 0) {
                companyRow = await dbContext.find(esWB_Company, jpr.WB_COMPANY_ID);
            }
            if (!_.isEmpty(companyRow)) {
                pJobParamRow.cif.WB_COMPANY_ID = companyRow.cif.WB_COMPANY_ID;
                pJobParamRow.WB_CompanyRow = companyRow;
            }
        }

        //--- 3. SalesOrg
        if (_.isEmpty(pJobParamRow.WB_SalesOrgRow)) {
            if (Number(opts?.wbSalesOrgId) > 0) {
                let sor = <WB_SalesOrgRow>await dbContext.find(esWB_SalesOrg, opts.wbSalesOrgId);
                if (!_.isEmpty(sor)) {
                    pJobParamRow.cif.WB_SALESORG_ID = sor.cif.WB_SALESORG_ID;
                    pJobParamRow.WB_SalesOrgRow = sor;
                }
            } else {
                let sor = <WB_SalesOrgRow>await dbContext.find(esWB_SalesOrg, jpr.WB_SALESORG_ID);
                if (!_.isEmpty(sor)) pJobParamRow.WB_SalesOrgRow = sor;
            }
        }

        //--- 4. App Exec
        if (_.isEmpty(pJobParamRow.WB_UIAppExecutableRow)) {
            let appExecId = opts?.appExecId || "";
            if (!_.isEmpty(appExecId)) {
                let execRows = <WB_UIAppExecutableRow[]>await dbContext.findAll(esWB_UI_AppExecutable, {
                    where: {
                        APPEXEC_ID: appExecId,
                        DELETED_IND: { neq: "1" },
                    },
                });
                if (execRows.length) {
                    let execRow = _.first(execRows);
                    pJobParamRow.cif.WB_UI_APPEXEC_ID_TARGET = execRow.cif.WB_UI_APPEXEC_ID;
                    pJobParamRow.WB_UIAppExecutableRow = execRow;
                }
            } else {
                if (jpr.cif.WB_UI_APPEXEC_ID_TARGET > 0) {
                    let execRow = <WB_UIAppExecutableRow>(
                        await dbContext.find(esWB_UI_AppExecutable, jpr.cif.WB_UI_APPEXEC_ID_TARGET)
                    );
                    if (!_.isEmpty(execRow)) pJobParamRow.WB_UIAppExecutableRow = execRow;
                }
            }
        }
    }

    private async findByCondition(cndFields: any, options: { dbContext: PAdbContext }): Promise<WB_JobParamRow[]> {
        let dbContext = options?.dbContext;
        if (_.isEmpty(dbContext)) dbContext = new PAdbContext();

        let rr = new ESRecordResolver(dbContext, {
            standardConditions: {
                [esWB_Job_ParamSR]: { DELETED_IND: { notIn: ["1"] } },
            },
        });

        return <WB_JobParamRow[]>await rr.queryAllWithRelationships(esWB_Job_Param, { where: cndFields });
    }
}
